// MACHINERY V2 (Stroje) — the profile hero BELOW 1280 (M2.16d, boards 20 + 21).
//
// >>> THIS FILE IS THE ANSWER TO A COMMENT THAT WENT STALE. <<< ProfileHero.tsx
// carried "M2.11 owns the real phone/tablet pass for this hero" beside its
// flexWrap seatbelt. M2.11 was superseded by the M2.16 mobile sweep, and M2.16d
// is that pass; the stale line is corrected at its site.
//
// WHAT THE BOARDS DRAW (20 and 21 draw the same band):
//     [thumb]  Trens SBL 500 CNC            [KATALÓG]
//              S/N AB859034
// A compact identity band — not the desktop card rotated, and not a shrunken
// copy of it. The desktop hero's 170x112 photo, 26px h1, 190px action column and
// full §2 chip/KV block do not fit a 390px viewport at any scale.
//
// ============================================================
//  THREE DELIBERATE DEVIATIONS FROM THE BOARD, EACH BECAUSE THE BOARD DID NOT
//  CONSIDER SOMETHING THE PAGE OWES THE USER
// ============================================================
//
//  1. THE ACTIONS ARE KEPT. The boards show the band with no buttons and no
//     kebab. Shipping that literally would delete the ONLY phone access to the
//     QR code, decommission, duplicate and "new service request" — the kebab is
//     not decoration, it is the machine's whole action set (useProfileActions
//     .ts, gated by buildFleetActions). They are re-composed instead: the two
//     primaries become full-width rows under the identity line and the kebab
//     sits beside the catalog pill. Gating is INHERITED, never re-derived — each
//     control renders only when the action set contains its entry, so a Worker
//     and a decommissioned machine collapse exactly as they do on the fleet.
//
//  2. THE §2 IDENTITY FIELDS ARE KEPT, BEHIND A DISCLOSURE. The band shows only
//     the S/N. Data-package §2 field parity is BINDING (manager ruling
//     2026-08-22: the designer layout wins on STRUCTURE, §2 parity stays FULL),
//     so location, groups, taper, control system, year and the spec KV grid
//     cannot simply be dropped below 1280 — they would be unreachable on a
//     phone, on the one page that owns them. ProfileIdentityRows is therefore
//     mounted unchanged inside a collapsed-by-default disclosure. Nothing is
//     lost, and the band keeps the board's proportions at rest.
//
//  3. THE LIVE RIBBON IS NOT REPEATED HERE. The desktop hero ends with
//     ProfileLiveRibbon; the boards' band does not, because board 20 moves that
//     exact content into the PREHĽAD tab's live status card directly below it.
//     Rendering both would print "BEŽÍ" twice, 40px apart. Prehľad is the
//     DEFAULT tab and is ungated (ruling Q1-M2.16), so the live state is one tap
//     away at worst and on screen by default at best. The suppression is
//     positional, not a second status rule: nothing here tests telemetry.
//
// ============================================================
//  TWO THINGS THE FIRST DRAFT DROPPED THAT WERE NOT DEVIATIONS — THEY WERE
//  INFORMATION LOSS, AND THEY ARE BACK (M2.16d review, item 3)
// ============================================================
//  · THE HEALTH DOT. `HealthDot` is the open-service-report signal, and the
//    band is the phone's ONLY view of this machine's header. Without it a phone
//    user could not see that a machine has open reports on the very page that
//    owns that fact — and the desktop hero shows it. It is composed exactly as
//    §2 requires: the decommissioned pill REPLACES it, so the two are a ternary
//    and can never appear together.
//  · THE EYEBROW. It carries the derived type line and, for a machine with no
//    catalog model, the words "custom build". The band marks the CATALOG case
//    with the KATALÓG pill and marked its complement with nothing at all, so a
//    custom machine was silently indistinguishable. It renders from
//    profileHeroAtoms' `HeroEyebrow`, the same element the desktop hero draws —
//    restored, not re-derived.
//  Neither was ever listed among the deviations above, which is what made them
//  losses rather than decisions. There are still exactly three deviations.
//
// THE §2 CHIP ROW IS NOT DRAWN TWICE EITHER (review item 2). The band's
// top-right KATALÓG pill and the disclosure's chip row were both painting the
// same pill from the same gate and the same i18n key, so expanding the
// disclosure showed KATALÓG twice about 120px apart. The band keeps the board's
// pill; ProfileIdentityRows is told to omit its copy HERE and nowhere else.
//
// It renders for the WHOLE below-1280 band, phone AND tablet, not just < 768.
// Protections attach to SURFACES, not files: a 1024px tablet is a touch device
// and the desktop hero's 190px action column plus 170px photo is a pointer
// layout. The one predicate is `V2_BELOW_DESKTOP`, read once by ProfileBody.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../shared/components/gallery";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import FleetCardMenu, { FleetMenuAction } from "../fleet/FleetCardMenu";
import { HealthDot, MachinePhoto } from "../fleet/fleetCardAtoms";
import ProfileIdentityRows from "./ProfileIdentityRows";
import { CatalogPill, DecommissionedPill, HeroEyebrow } from "./profileHeroAtoms";

export interface ProfileHeroPhoneProps {
  machine: Machine;
  actions: FleetMenuAction[];
  onCreateServiceReport: (() => void) | null;
  onCreateServiceRequest: (() => void) | null;
}

const ProfileHeroPhone: React.FC<ProfileHeroPhoneProps> = ({
  machine,
  actions,
  onCreateServiceReport,
  onCreateServiceRequest,
}) => {
  const { translate } = useTranslation("myMachinery");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const decommissioned = machine.isDecommissioned === true;

  // The SHARED 4-tier fallback (§2): files[] -> image -> catalog images ->
  // catalog hero URL. The same call the desktop hero and the fleet card make;
  // never re-implemented.
  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  return (
    <section
      style={{
        background: "var(--card)",
        border: decommissioned ? "1px dashed var(--border)" : "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "var(--shadow)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
        opacity: decommissioned ? 0.75 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <MachinePhoto
          src={images.length > 0 ? images[0].src : null}
          alt={getMachineDisplayName(machine)}
          grayscale={decommissioned}
          width={62}
          height={44}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <HeroEyebrow machine={machine} />
          <h1
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              minWidth: 0,
              wordBreak: "break-word",
            }}
          >
            {getMachineDisplayName(machine)}
          </h1>
          {machine.serialNumber && (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10.5,
                color: "var(--faint)",
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {translate("myMachinery:sn")} {machine.serialNumber}
            </div>
          )}
        </div>

        {/* Board 20/21's top-right cluster. §2 keeps the decommissioned pill
            and the catalog pill as separate facts, so a retired CATALOG machine
            shows both. What the decommissioned pill REPLACES is the HEALTH DOT
            — the same mutual exclusion HeroNameRow composes on the desktop, so
            the two surfaces cannot disagree about a retired machine. */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
        >
          {decommissioned ? (
            <DecommissionedPill />
          ) : (
            <HealthDot openReports={machine.openServiceReportsCount} />
          )}
          {machine.catalogModelId && <CatalogPill />}
          {actions.length > 0 && (
            <FleetCardMenu
              actions={actions}
              ariaLabel={translate("myMachinery:v2.profile.actionsMenu")}
            />
          )}
        </div>
      </div>

      {(onCreateServiceReport || onCreateServiceRequest) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          {onCreateServiceReport && (
            <button
              type="button"
              className="btn btn-mod"
              style={{ justifyContent: "center", width: "100%" }}
              onClick={onCreateServiceReport}
            >
              {"＋ " + translate("myMachinery:v2.profile.newReport")}
            </button>
          )}
          {onCreateServiceRequest && (
            <button
              type="button"
              className="btn"
              style={{ justifyContent: "center", width: "100%" }}
              onClick={onCreateServiceRequest}
            >
              {"＋ " + translate("myMachinery:v2.profile.newRequest")}
            </button>
          )}
        </div>
      )}

      {/* Deviation 2 — §2 parity, collapsed. The 44px floor is explicit rather
          than inherited from the stylesheet, because this control exists ONLY
          below 1280 and its target is not something a later reader should have
          to look up in a media query to confirm. */}
      <button
        type="button"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((prev) => !prev)}
        style={{
          minHeight: 44,
          minWidth: 44,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: 0,
          border: 0,
          background: "transparent",
          font: "inherit",
          fontSize: 12.5,
          color: "var(--mod-accent)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {translate("myMachinery:v2.profile.machineDetails")}
        <svg
          viewBox="0 0 12 12"
          width="11"
          height="11"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden
          style={{
            // Final resting state is correct with the transform applied and no
            // animation ever running — machinery-v2-tokens.scss:289-292 forces
            // `transition: none !important` under prefers-reduced-motion.
            transform: detailsOpen ? "rotate(180deg)" : "none",
          }}
        >
          <path d="M3 4.5 L6 7.5 L9 4.5" />
        </svg>
      </button>

      {/* `omitCatalogPill` — the band above already paints that exact pill from
          the same gate and the same key, so the disclosure must not paint a
          second one 120px below it (review item 2). */}
      {detailsOpen && <ProfileIdentityRows machine={machine} omitCatalogPill />}
    </section>
  );
};

export default ProfileHeroPhone;
